import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CreditCard, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dropdown that fetches all active Payment Terms from the CMS and, on select,
 * appends (or replaces) the body of the chosen term into an existing text.
 *
 * Props:
 *   - currentValue: string (current value of the target field, e.g. formData.terms)
 *   - onInsert: (newValue: string) => void — called with the concatenated value
 *   - mode: "append" (default) or "replace"
 */
const PaymentTermPicker = ({ currentValue = '', onInsert, mode = 'append', testId }) => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/cms/payment-terms/`);
        if (active) setTerms(r.data || []);
      } catch (_) {
        if (active) setTerms([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleSelect = (e) => {
    const code = e.target.value;
    if (!code) return;
    const term = terms.find((t) => t.code === code);
    if (!term) return;
    const body = (term.body || '').trim();
    if (!body) return;
    if (mode === 'replace' || !currentValue?.trim()) {
      onInsert(body);
    } else {
      const sep = currentValue.endsWith('\n') ? '' : '\n';
      onInsert(`${currentValue}${sep}${body}`);
    }
    // Reset selection so the admin can pick again if needed
    e.target.value = '';
  };

  return (
    <div className="flex items-center gap-2" data-testid={testId || 'payment-term-picker'}>
      <CreditCard className="w-4 h-4 text-[#023E8A] shrink-0" />
      <select
        onChange={handleSelect}
        disabled={loading}
        defaultValue=""
        className="flex-1 px-2 py-1.5 border rounded text-xs bg-white hover:border-[#023E8A] disabled:opacity-50"
      >
        <option value="">
          {loading ? 'Loading payment terms…' : `Insert a payment term (${terms.length})`}
        </option>
        {terms.map((t) => (
          <option key={t.code} value={t.code}>
            {t.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default PaymentTermPicker;
